// Splits Slack mrkdwn into inline pieces, so a card can draw mentions as pills,
// which the Markdown renderer cannot. The pieces cover the spans Slack itself draws
// inline: mentions, channels, links, bold, italic, strike and code
export type MrkdwnSegment =
  | { kind: 'text'; text: string }
  | { kind: 'mention'; name: string; source: string }
  | { kind: 'channel'; name: string; source: string }
  | { kind: 'link'; href: string; text: string }
  | { kind: 'bold'; text: string }
  | { kind: 'italic'; text: string }
  | { kind: 'strike'; text: string }
  | { kind: 'code'; text: string };

const TOKEN = new RegExp(
  [
    /<@([A-Z0-9]+)(?:\|([^>]+))?>/.source,
    /<!(here|channel|everyone)>/.source,
    /<(https?:\/\/[^|>]+)(?:\|([^>]+))?>/.source,
    /(```[\s\S]*?```|`[^`\n]+`)/.source,
    /(?<![*\w])\*([^*\n]+)\*(?![*\w])/.source,
    /(?<![_\w])_([^_\n]+)_(?![_\w])/.source,
    /(?<![~\w])~([^~\n]+)~(?![~\w])/.source,
    /(?<![\w@])([\w.+-]+@[\w.-]+\.[a-z]{2,})\b/i.source,
    /(?<![\w@/])((?:https?:\/\/|(?:[a-z0-9-]+\.)+[a-z]{2,}\/)[^\s<>`]*[^\s<>`.,!?:;)\]])/i
      .source,
    /(?<![\w@/])@([\w][\w.-]*[\w]|\w)\b/.source,
    /<#[A-Z0-9]+\|([^>]+)>/.source,
  ].join('|'),
  'g'
);

function segmentOf(match: RegExpMatchArray): MrkdwnSegment {
  const [
    ,
    userId,
    userName,
    broadcast,
    href,
    linkText,
    code,
    bold,
    italic,
    strike,
    email,
    url,
    displayName,
    channelName,
  ] = match;
  if (userId)
    return { kind: 'mention', name: userName ?? userId, source: match[0] };
  if (broadcast) return { kind: 'mention', name: broadcast, source: match[0] };
  if (href) return { kind: 'link', href, text: linkText ?? href };
  if (code !== undefined) {
    const fence = code.startsWith('```') ? 3 : 1;
    return { kind: 'code', text: code.slice(fence, -fence) };
  }
  if (bold !== undefined) return { kind: 'bold', text: bold };
  if (italic !== undefined) return { kind: 'italic', text: italic };
  if (email) return { kind: 'text', text: email };
  if (url)
    return {
      kind: 'link',
      href: /^https?:\/\//.test(url) ? url : `https://${url}`,
      text: url,
    };
  if (displayName)
    return { kind: 'mention', name: displayName, source: match[0] };
  if (channelName)
    return { kind: 'channel', name: channelName, source: match[0] };
  return { kind: 'strike', text: strike ?? '' };
}

export function mrkdwnSegments(text: string): MrkdwnSegment[] {
  const segments: MrkdwnSegment[] = [];
  let textStart = 0;
  for (const match of text.matchAll(TOKEN)) {
    if (match.index > textStart) {
      const previous = segments.at(-1);
      const plain = text.slice(textStart, match.index);
      if (previous?.kind === 'text') previous.text += plain;
      else segments.push({ kind: 'text', text: plain });
    }
    const segment = segmentOf(match);
    const previous = segments.at(-1);
    if (segment.kind === 'text' && previous?.kind === 'text')
      previous.text += segment.text;
    else segments.push(segment);
    textStart = match.index + match[0].length;
  }
  if (textStart < text.length) {
    const previous = segments.at(-1);
    if (previous?.kind === 'text') previous.text += text.slice(textStart);
    else segments.push({ kind: 'text', text: text.slice(textStart) });
  }
  return segments;
}
